import { logger } from 'common/services/logger';

export type PeerConnectionListener = (pc: RTCPeerConnection) => void;

type RTCPeerConnectionCtor = typeof RTCPeerConnection;
type Subscription = { listener: PeerConnectionListener; onRemove?: PeerConnectionListener };

const pending = new Set<RTCPeerConnection>();
const live = new Set<RTCPeerConnection>();
const subscriptions = new Set<Subscription>();
let installed = false;
let original: RTCPeerConnectionCtor | undefined;

// A throwing subscriber must never break the SDK's call into the connection, nor starve the
// other subscribers.
const safeInvoke = (fn: PeerConnectionListener, pc: RTCPeerConnection) => {
  try {
    fn(pc);
  } catch (error) {
    logger.warn('[peerConnectionRegistry] listener threw', { error });
  }
};

const forget = (pc: RTCPeerConnection) => {
  if (live.delete(pc)) {
    subscriptions.forEach((sub) => {
      if (sub.onRemove) safeInvoke(sub.onRemove, pc);
    });
  }
};

const deliver = (pc: RTCPeerConnection) => {
  pending.delete(pc);
  live.add(pc);
  subscriptions.forEach((sub) => safeInvoke(sub.listener, pc));
};

type DescriptionMethod = 'setLocalDescription' | 'setRemoteDescription';

// Delivers a pending connection on its first local or remote description, then defers to the
// original method with the caller's arguments and its return value untouched. The arguments are
// forwarded opaquely because both methods are overloaded (the legacy callback form), which
// Parameters<> cannot express; the property keeps its DOM type for every caller.
const wrapDescription = <M extends DescriptionMethod>(pc: RTCPeerConnection, method: M) => {
  const originalMethod = pc[method].bind(pc) as (...args: unknown[]) => Promise<void>;
  const wrapped = async (...args: unknown[]): Promise<void> => {
    if (pending.has(pc)) {
      deliver(pc);
    }
    return originalMethod(...args);
  };
  pc[method] = wrapped as RTCPeerConnection[M];
};

// Throws if the connection's methods cannot be bound (a non-standard shim); the wrapper's
// constructor catches that. `pending` is only joined once every method is patched, so a
// half-patched connection is never delivered.
const remember = (pc: RTCPeerConnection) => {
  const originalClose = pc.close.bind(pc);
  pc.close = () => {
    pending.delete(pc);
    forget(pc);
    originalClose();
  };

  wrapDescription(pc, 'setLocalDescription');
  wrapDescription(pc, 'setRemoteDescription');

  pending.add(pc);
};

/**
 * Wraps the global RTCPeerConnection so every connection the Agora SDK constructs is known to us.
 * Must run before any module that can construct a connection is evaluated; index.tsx calls it
 * first thing. The wrapper keeps the constructor arguments and the prototype chain of whatever
 * global it wraps (on Safari, Agora's adapter shim replaces the global with a factory function,
 * so `instanceof` may not be preserved for wrapped instances).
 */
const install = () => {
  if (installed) return;
  const Original = (window as unknown as { RTCPeerConnection?: RTCPeerConnectionCtor }).RTCPeerConnection;
  if (typeof Original !== 'function') return;

  original = Original;
  const Wrapped = class extends Original {
    constructor(...args: ConstructorParameters<RTCPeerConnectionCtor>) {
      super(...args);
      // Nothing here may throw into the SDK's `new RTCPeerConnection(...)`: an untracked
      // connection only costs us its stats.
      try {
        remember(this);
      } catch (error) {
        logger.warn('[peerConnectionRegistry] could not track a peer connection', { error });
      }
    }
  };
  (window as unknown as { RTCPeerConnection: RTCPeerConnectionCtor }).RTCPeerConnection = Wrapped;
  installed = true;
};

/**
 * Installs the wrapper only when ObserveRTC is configured. index.html loads env-config.js before
 * the module script, and window._env_ cannot change without a reload, so with the key unset the
 * global RTCPeerConnection is never touched.
 */
const installIfConfigured = (observerApiUrl: string | undefined) => {
  if (observerApiUrl) install();
};

const getLive = (): RTCPeerConnection[] => [...live];

/** Replays every live connection to the listener, then delivers future ones until unsubscribed. */
const subscribe = (listener: PeerConnectionListener, onRemove?: PeerConnectionListener): (() => void) => {
  const subscription: Subscription = { listener, onRemove };
  subscriptions.add(subscription);

  live.forEach((pc) => safeInvoke(listener, pc));

  return () => {
    subscriptions.delete(subscription);
  };
};

const _resetForTests = () => {
  pending.clear();
  live.clear();
  subscriptions.clear();
  if (installed && original) {
    (window as unknown as { RTCPeerConnection: RTCPeerConnectionCtor }).RTCPeerConnection = original;
  }
  installed = false;
  original = undefined;
};

export const peerConnectionRegistry = { install, installIfConfigured, getLive, subscribe, _resetForTests };
